import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, DatePicker, Form, Input, InputNumber, Select, Space, Switch, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';

const { Title } = Typography;

function useClassOptions() {
  const classQ = useQuery({
    queryKey: ['pmv-classifications'],
    queryFn: () => pmvService.listClassifications(),
  });
  const options = (classQ.data ?? []).map((c) => ({
    value: c.id,
    label: `${c.code} — ${c.name}`,
  }));
  return { options, loading: classQ.isLoading };
}

export const PmvInspectionTemplateFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { options } = useClassOptions();

  const mut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvService.createInspectionTemplate(body),
    onSuccess: () => {
      message.success('Inspection template created');
      navigate('/asset/pmv/setup');
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  return (
    <WorkspaceLayout header={<EnterprisePageHeader documentLabel="New inspection template" documentNumber="" />}>
      <Title level={5}>PMV inspection template</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        initialValues={{ inspection_kind: 'PRE_USE', fail_quarantine_policy: 'ALWAYS' }}
        onFinish={(v) => {
          const items = String(v.checklist_items || '')
            .split('\n')
            .map((line: string) => line.trim())
            .filter(Boolean)
            .map((line: string, idx: number) => {
              const [code, ...rest] = line.split('|');
              return {
                code: (code ?? `item_${idx + 1}`).trim(),
                label: (rest.join('|') || code).trim(),
                mandatory: true,
              };
            });
          mut.mutate({
            template_code: v.template_code,
            template_name: v.template_name,
            classification_id: v.classification_id ?? null,
            inspection_kind: v.inspection_kind,
            frequency_days: v.frequency_days ?? null,
            fail_quarantine_policy: v.fail_quarantine_policy,
            checklist_items_json: items.length
              ? items
              : [{ code: 'general', label: 'General check', mandatory: true }],
          });
        }}
      >
        <Form.Item name="template_code" label="Template code" rules={[{ required: true }]}>
          <Input placeholder="EXC-PREUSE" />
        </Form.Item>
        <Form.Item name="template_name" label="Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="classification_id" label="Classification">
          <Select allowClear placeholder="All classes" options={options} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="inspection_kind" label="Kind" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'PRE_USE', label: 'Pre-use' },
              { value: 'PERIODIC', label: 'Periodic' },
              { value: 'RETURN', label: 'Return' },
              { value: 'AD_HOC', label: 'Ad hoc' },
            ]}
          />
        </Form.Item>
        <Form.Item name="frequency_days" label="Frequency (days, periodic only)">
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="fail_quarantine_policy" label="Fail policy">
          <Select
            options={[
              { value: 'ALWAYS', label: 'Always quarantine' },
              { value: 'NEVER', label: 'Never quarantine' },
              { value: 'BY_SEVERITY', label: 'Critical items only' },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="checklist_items"
          label="Checklist items (one per line: code|label)"
          extra="Example: brakes|Brakes functional"
        >
          <Input.TextArea rows={4} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Create
          </Button>
          <Button onClick={() => navigate('/asset/pmv/setup')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const PmvDocumentRequirementFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { options } = useClassOptions();

  const mut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvService.createDocumentRequirement(body),
    onSuccess: () => {
      message.success('Document requirement created');
      navigate('/asset/pmv/setup');
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  return (
    <WorkspaceLayout header={<EnterprisePageHeader documentLabel="New document requirement" documentNumber="" />}>
      <Title level={5}>PMV document requirement</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        initialValues={{ document_category: 'REGISTRATION', is_mandatory: true, blocks_deployment: true }}
        onFinish={(v) => mut.mutate(v)}
      >
        <Form.Item name="requirement_code" label="Code" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="classification_id" label="Classification">
          <Select allowClear options={options} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="document_category" label="Document category">
          <Select
            options={[
              { value: 'REGISTRATION', label: 'Registration' },
              { value: 'INSURANCE', label: 'Insurance' },
              { value: 'PERMIT', label: 'Permit' },
              { value: 'CERTIFICATE', label: 'Certificate' },
              { value: 'OTHER', label: 'Other' },
            ]}
          />
        </Form.Item>
        <Form.Item name="document_entity_type" label="Entity type hint">
          <Select
            allowClear
            options={[
              { value: 'Asset', label: 'Asset' },
              { value: 'PMV_PROFILE', label: 'PMV Profile' },
            ]}
          />
        </Form.Item>
        <Form.Item name="is_mandatory" label="Mandatory" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="blocks_deployment" label="Blocks deployment" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Create
          </Button>
          <Button onClick={() => navigate('/asset/pmv/setup')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const PmvInternalHireRateFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { options } = useClassOptions();

  const rateQ = useQuery({
    queryKey: ['pmv-internal-hire-rates'],
    queryFn: () => pmvService.listInternalHireRates(),
    enabled: isEdit,
  });

  useEffect(() => {
    if (!isEdit || !rateQ.data) return;
    const row = rateQ.data.find((r) => r.id === id);
    if (!row) return;
    form.setFieldsValue({
      rate_code: row.rate_code,
      label: row.label,
      classification_id: row.classification_id ?? undefined,
      rate_per_hour: row.rate_per_hour,
      rate_per_day: row.rate_per_day ?? undefined,
      currency: row.currency,
      effective_from: row.effective_from ? dayjs(row.effective_from) : undefined,
      effective_until: row.effective_until ? dayjs(row.effective_until) : undefined,
      is_active: row.is_active !== false,
    });
  }, [form, id, isEdit, rateQ.data]);

  const mut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      isEdit ? pmvService.patchInternalHireRate(id!, body) : pmvService.createInternalHireRate(body),
    onSuccess: () => {
      message.success(isEdit ? 'Hire rate updated' : 'Internal hire rate created');
      navigate('/asset/pmv/setup');
    },
    onError: (e: Error) => message.error(e.message || (isEdit ? 'Update failed' : 'Create failed')),
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? 'Edit internal hire rate' : 'New internal hire rate'}
          documentNumber=""
        />
      }
    >
      <Title level={5}>Internal hire rate</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        initialValues={{ currency: 'AED', is_active: true, effective_from: dayjs() }}
        onFinish={(v) => {
          const payload: Record<string, unknown> = {
            label: v.label,
            rate_per_hour: v.rate_per_hour,
            rate_per_day: v.rate_per_day ?? null,
            currency: v.currency || 'AED',
            effective_from: (v.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
            effective_until: v.effective_until
              ? (v.effective_until as dayjs.Dayjs).format('YYYY-MM-DD')
              : null,
            is_active: v.is_active !== false,
          };
          if (!isEdit) {
            payload.rate_code = v.rate_code;
            payload.classification_id = v.classification_id ?? null;
          }
          mut.mutate(payload);
        }}
      >
        <Form.Item name="rate_code" label="Rate code" rules={[{ required: true }]}>
          <Input placeholder="EXC-DEFAULT" disabled={isEdit} />
        </Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="classification_id" label="Classification">
          <Select
            allowClear
            placeholder="Company default (any class)"
            options={options}
            showSearch
            optionFilterProp="label"
            disabled={isEdit}
          />
        </Form.Item>
        <Form.Item name="rate_per_hour" label="Rate per hour" rules={[{ required: true }]}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="rate_per_day" label="Rate per day">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="currency" label="Currency">
          <Input maxLength={10} />
        </Form.Item>
        <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="effective_until" label="Effective until">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
          <Button onClick={() => navigate('/asset/pmv/setup')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const PmvClassificationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const classQ = useQuery({
    queryKey: ['pmv-classifications'],
    queryFn: () => pmvService.listClassifications(),
  });

  useEffect(() => {
    if (!isEdit || !classQ.data) return;
    const row = classQ.data.find((c) => c.id === id);
    if (!row) return;
    form.setFieldsValue({
      code: row.code,
      name: row.name,
      class_family: row.class_family,
      parent_id: row.parent_id ?? undefined,
      description: row.description ?? undefined,
      sort_order: row.sort_order ?? 100,
      is_active: row.is_active !== false,
    });
  }, [isEdit, id, classQ.data, form]);

  const createMut = useMutation({
    mutationFn: (body: Parameters<typeof pmvService.createClassification>[0]) =>
      pmvService.createClassification(body),
    onSuccess: () => {
      message.success('Equipment class created');
      navigate('/asset/pmv/setup');
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const patchMut = useMutation({
    mutationFn: (body: Parameters<typeof pmvService.patchClassification>[1]) =>
      pmvService.patchClassification(id!, body),
    onSuccess: () => {
      message.success('Equipment class updated');
      navigate('/asset/pmv/setup');
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  const parentOptions = (classQ.data ?? [])
    .filter((c) => c.id !== id)
    .map((c) => ({
      value: c.id,
      label: `${c.code} — ${c.name}`,
    }));

  const row = classQ.data?.find((c) => c.id === id);
  const codeLocked = Boolean(row?.is_system);

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? 'Edit equipment class' : 'New equipment class'}
          documentNumber=""
        />
      }
    >
      <Title level={5}>PMV equipment class</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        initialValues={{ class_family: 'MACHINERY', sort_order: 100, is_active: true }}
        onFinish={(v) => {
          const base = {
            name: v.name as string,
            class_family: v.class_family as 'PLANT' | 'MACHINERY' | 'VEHICLE',
            parent_id: (v.parent_id as string | undefined) ?? null,
            description: (v.description as string | undefined) ?? null,
            sort_order: (v.sort_order as number | undefined) ?? 100,
          };
          if (isEdit) {
            patchMut.mutate({
              ...base,
              ...(codeLocked
                ? {}
                : {
                    code: String(v.code || '')
                      .trim()
                      .toUpperCase()
                      .replace(/\s+/g, '_'),
                  }),
              is_active: Boolean(v.is_active),
            });
          } else {
            createMut.mutate({
              ...base,
              code: String(v.code || '')
                .trim()
                .toUpperCase()
                .replace(/\s+/g, '_'),
            });
          }
        }}
      >
        <Form.Item
          name="code"
          label="Code"
          rules={[{ required: true, message: 'Code is required' }]}
          extra={codeLocked ? 'System starter code cannot be changed' : 'Unique per company, e.g. ROLLER'}
        >
          <Input placeholder="ROLLER" disabled={codeLocked} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="Roller" />
        </Form.Item>
        <Form.Item name="class_family" label="Family" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'PLANT', label: 'Plant' },
              { value: 'MACHINERY', label: 'Machinery' },
              { value: 'VEHICLE', label: 'Vehicle' },
            ]}
          />
        </Form.Item>
        <Form.Item name="parent_id" label="Parent class" extra="Optional hierarchy (e.g. under Machinery)">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            loading={classQ.isLoading}
            options={parentOptions}
            placeholder="None"
          />
        </Form.Item>
        <Form.Item name="sort_order" label="Sort order">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} />
        </Form.Item>
        {isEdit ? (
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        ) : null}
        <Space>
          <Button type="primary" htmlType="submit" loading={createMut.isPending || patchMut.isPending}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
          <Button onClick={() => navigate('/asset/pmv/setup')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};
